import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SIGNALING_SERVER = process.env.REACT_APP_SIGNALING_SERVER || 'https://sosexpert-api.onrender.com/';

export const useWebRTC = (sessionId, role) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [logs, setLogs] = useState([]);

  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);

  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ]
  };

  const log = useCallback((msg) => {
    console.log(`[${role}] ${msg}`);
    setLogs(prev => [...prev.slice(-20), `${new Date().toLocaleTimeString()} ${msg}`]);
  }, [role]);

  const startCamera = useCallback(async () => {
    try {
      log('Demande camera...');

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('getUserMedia non disponible. Utilise HTTPS ou localhost.');
      }

      const preferredMode = role === 'operator' ? 'environment' : 'user';
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: preferredMode } },
          audio: true,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      }
      log('Camera obtenue: ' + stream.getTracks().map(t => t.kind).join(', '));

      localStreamRef.current = stream;
      setLocalStream(stream);
      setCameraError(null);
      return stream;

    } catch (err) {
      log('ERREUR camera: ' + err.name + ' - ' + err.message);
      setCameraError(err.message);
      return null;
    }
  }, [role, log]);

  const createPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      log('Fermeture ancienne PeerConnection');
      peerConnectionRef.current.close();
    }

    const pc = new RTCPeerConnection(iceServers);
    peerConnectionRef.current = pc;

    pc.ontrack = (event) => {
      log('Track recu !');
      const [stream] = event.streams;
      if (stream) {
        setRemoteStream(stream);
        log('Stream distant defini');
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice-candidate', { candidate: event.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      log('WebRTC state: ' + pc.connectionState);
      setConnectionState(pc.connectionState);
    };

    pc.onicegatheringstatechange = () => {
      log('ICE gathering: ' + pc.iceGatheringState);
    };

    pc.oniceconnectionstatechange = () => {
      log('ICE connection: ' + pc.iceConnectionState);
    };

    return pc;
  }, [log]);

  useEffect(() => {
    if (!sessionId || !role) return;

    let mounted = true;

    const init = async () => {
      log('Demarrage session: ' + sessionId);
      const stream = await startCamera();

      if (!mounted) return;

      log('Connexion socket vers: ' + SIGNALING_SERVER);
      socketRef.current = io(SIGNALING_SERVER, {
        transports: ['websocket', 'polling']
      });

      socketRef.current.on('connect', () => {
        log('Socket connecte: ' + socketRef.current.id);
        socketRef.current.emit('join-session', { sessionId, role });
        log('join-session emis: ' + sessionId + ' / ' + role);
      });

      socketRef.current.on('connect_error', (err) => {
        log('ERREUR socket: ' + err.message);
      });

      socketRef.current.on('peer-connected', async ({ role: peerRole }) => {
        log('PEER CONNECTE: ' + peerRole);
        setIsConnected(true);

        if (role === 'operator') {
          const currentStream = localStreamRef.current;
          if (!currentStream) {
            log('ERREUR: pas de stream pour offer');
            return;
          }

          log('Creation offer...');
          const pc = createPeerConnection();
          currentStream.getTracks().forEach(track => pc.addTrack(track, currentStream));

          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socketRef.current.emit('offer', { offer });
          log('Offer envoyee');
        }
      });

      socketRef.current.on('peer-disconnected', () => {
        log('Peer deconnecte');
        setIsConnected(false);
        setConnectionState('disconnected');
      });

      socketRef.current.on('offer', async (data) => {
        log('Offer recue');
        const currentStream = localStreamRef.current;
        if (!currentStream) {
          log('ERREUR: pas de stream pour repondre');
          return;
        }

        const pc = createPeerConnection();
        currentStream.getTracks().forEach(track => pc.addTrack(track, currentStream));

        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socketRef.current.emit('answer', { answer });
        log('Answer envoyee');
      });

      socketRef.current.on('answer', async (data) => {
        log('Answer recue');
        if (peerConnectionRef.current) {
          await peerConnectionRef.current.setRemoteDescription(
            new RTCSessionDescription(data.answer)
          );
          log('WebRTC etabli !');
        }
      });

      socketRef.current.on('ice-candidate', async (data) => {
        if (peerConnectionRef.current && data.candidate) {
          try {
            await peerConnectionRef.current.addIceCandidate(
              new RTCIceCandidate(data.candidate)
            );
          } catch (e) {
            log('ICE candidate ignore: ' + e.message);
          }
        }
      });
    };

    init();

    return () => {
      mounted = false;
      localStreamRef.current?.getTracks().forEach(track => track.stop());
      peerConnectionRef.current?.close();
      socketRef.current?.disconnect();
    };
  }, [sessionId, role, startCamera, createPeerConnection, log]);

  useEffect(() => {
    if (localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteStream && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  return {
    localStream,
    remoteStream,
    connectionState,
    isConnected,
    cameraError,
    localVideoRef,
    remoteVideoRef,
    socketRef,
    logs,
    peerConnectionRef,
  };
};